import React, { useState, useEffect } from 'react';
import { 
  FiX, FiSave,  FiUpload, 
   FiInfo, FiAlertCircle, FiCheckCircle, 
  FiTrash2, FiPaperclip, FiFileText, FiDownload,
  FiEye, FiShield
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../api';

const AssignmentModal = ({ isOpen, onClose, onSuccess, classItem, subject, labBatch, facultyId, editingAssignment }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    dueDate: '',
    dueTime: '23:59',
    maxMarks: '',
    weightage: '',
    allowLateSubmissions: false,
    lateSubmissionPenalty: 0,
    gracePeriodMinutes: 0,
    maxFileSizeMB: 10,
    allowedFileTypes: 'pdf,doc,docx,txt,jpg,jpeg,png',
    maxFilesPerSubmission: 5,
    isGroupAssignment: false,
    groupSize: 1,
    isPublished: false,
    visibleToStudents: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // Holds local files for new assignments
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [assignmentId, setAssignmentId] = useState(null);
  const [isNewAssignment, setIsNewAssignment] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // ============================================================
  // EFFECT: Initialize form when modal opens
  // ============================================================
  useEffect(() => {
    if (editingAssignment) {
      setIsNewAssignment(false);
      setAssignmentId(editingAssignment._id);
      
      setFormData({
        title: editingAssignment.title || '',
        description: editingAssignment.description || '',
        instructions: editingAssignment.instructions || '',
        dueDate: editingAssignment.dueDate ? new Date(editingAssignment.dueDate).toISOString().split('T')[0] : '',
        dueTime: editingAssignment.dueTime || '23:59',
        maxMarks: editingAssignment.maxMarks || '',
        weightage: editingAssignment.weightage || '',
        allowLateSubmissions: editingAssignment.allowLateSubmissions || false,
        lateSubmissionPenalty: editingAssignment.lateSubmissionPenalty || 0,
        gracePeriodMinutes: editingAssignment.gracePeriodMinutes || 0,
        maxFileSizeMB: editingAssignment.maxFileSizeMB || 10,
        allowedFileTypes: Array.isArray(editingAssignment.allowedFileTypes) 
          ? editingAssignment.allowedFileTypes.join(',') 
          : 'pdf,doc,docx,txt,jpg,jpeg,png',
        maxFilesPerSubmission: editingAssignment.maxFilesPerSubmission || 5,
        isGroupAssignment: editingAssignment.isGroupAssignment || false,
        groupSize: editingAssignment.groupSize || 1,
        isPublished: editingAssignment.isPublished || false,
        visibleToStudents: editingAssignment.visibleToStudents || false
      });
      
      if (editingAssignment.attachments && editingAssignment.attachments.length > 0) {
        setAttachments(editingAssignment.attachments);
      } else {
        setAttachments([]);
      }
      setPendingFiles([]);
    } else {
      setIsNewAssignment(true);
      setAssignmentId(null);
      
      const defaultDueDate = new Date();
      defaultDueDate.setDate(defaultDueDate.getDate() + 7);
      setFormData({
        title: '',
        description: '',
        instructions: '',
        dueDate: defaultDueDate.toISOString().split('T')[0],
        dueTime: '23:59',
        maxMarks: '',
        weightage: '',
        allowLateSubmissions: false,
        lateSubmissionPenalty: 0,
        gracePeriodMinutes: 0,
        maxFileSizeMB: 10,
        allowedFileTypes: 'pdf,doc,docx,txt,jpg,jpeg,png',
        maxFilesPerSubmission: 5,
        isGroupAssignment: false,
        groupSize: 1,
        isPublished: false,
        visibleToStudents: false
      });
      setAttachments([]);
      setPendingFiles([]);
    }
    setError(null);
    setSuccessMessage(null);
  }, [editingAssignment, isOpen]);

  // ============================================================
  // HELPER: Get public file URL
  // ============================================================
  const getPublicFileUrl = (fileUrl) => {
    if (!fileUrl) return '#';
    if (fileUrl.startsWith('http')) return fileUrl;
    
    let url = fileUrl;
    if (url.includes('/api/faculty/lms/file/')) {
      url = url.replace('/api/faculty/lms/file/', '/api/files/lms/');
    }
    
    if (!url.startsWith('http')) {
      url = `${API_BASE_URL}${url}`;
    }
    
    return url;
  };

  // ============================================================
  // HELPER: Format file size
  // ============================================================
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // ============================================================
  // Upload file to assignment
  // ============================================================
  const uploadFileToAssignment = async (file, targetAssignmentId) => {
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('tenantId', classItem?.tenantId || localStorage.getItem('tenantId'));
    uploadData.append('classId', classItem?.classId || classItem?._id);
    uploadData.append('subjectId', subject?.subjectId || subject?._id);
    uploadData.append('fileType', 'attachment');
    uploadData.append('category', 'attachments');

    const endpoint = `/api/files/lms/assignment/${targetAssignmentId}/attachment`;
    
    const response = await api.post(endpoint, uploadData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Upload failed');
    }

    return response.data;
  };

  // ============================================================
  // Add attachment connection structure
  // ============================================================
  const addAttachmentToAssignment = async (targetAssignmentId, fileData) => {
    const attachmentData = {
      attachments: [{
        name: fileData.originalName || fileData.name,
        fileId: fileData.fileId,
        fileUrl: fileData.fileUrl || `/api/files/lms/${encodeURIComponent(fileData.fileId)}`,
        size: fileData.size,
        type: fileData.mimeType || fileData.type,
        uploadedAt: fileData.uploadedAt || new Date().toISOString()
      }]
    };

    const response = await api.post(
      `/api/faculty/lms/assignment/${targetAssignmentId}/attachments`,
      attachmentData
    );

    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to add attachment');
    }

    return response.data;
  };

  // ============================================================
  // Handle file picker selection
  // ============================================================
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // NEW ASSIGNMENT: Save files locally to be pushed up during form submission
    if (isNewAssignment) {
      setPendingFiles(prev => [...prev, ...files]);
      e.target.value = '';
      return;
    }

    // EXISTING ASSIGNMENT: Upload directly right now
    if (!assignmentId) {
      setError('Assignment ID not found. Please save the assignment first.');
      return;
    }

    setUploadingFiles(true);
    setError(null);

    try {
      const uploadedFiles = [];
      for (const file of files) {
        try {
          const uploadResult = await uploadFileToAssignment(file, assignmentId);
          if (uploadResult.success) {
            const addResult = await addAttachmentToAssignment(assignmentId, uploadResult);
            uploadedFiles.push(addResult.data?.attachments?.[0] || uploadResult);
          }
        } catch (fileError) {
          console.error('[AssignmentModal] Error uploading file:', fileError);
          setError(`Failed to upload "${file.name}": ${fileError.message}`);
        }
      }

      if (uploadedFiles.length > 0) {
        setAttachments(prev => [...prev, ...uploadedFiles]);
        showNotification(`${uploadedFiles.length} file(s) uploaded successfully!`, 'success');
      }
      e.target.value = '';
    } catch (error) {
      console.error('[AssignmentModal] Upload error:', error);
      setError(error.response?.data?.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  // ============================================================
  // Remove attachment (from either pending state or server)
  // ============================================================
  const removeAttachment = async (index, isPending = false) => {
    if (isPending) {
      setPendingFiles(prev => prev.filter((_, i) => i !== index));
      return;
    }

    if (!assignmentId) {
      setAttachments(prev => prev.filter((_, i) => i !== index));
      return;
    }

    try {
      await api.delete(`/api/faculty/lms/assignment/${assignmentId}/attachments/${index}`);
      setAttachments(prev => prev.filter((_, i) => i !== index));
      showNotification('Attachment removed successfully', 'success');
    } catch (error) {
      console.error('[AssignmentModal] Error removing attachment:', error);
      setError('Failed to remove attachment');
    }
  };

  // ============================================================
  // Submit: Create or Update Assignment
  // ============================================================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const data = {
        classId: classItem?.classId || classItem?._id,
        subjectId: subject?.subjectId || subject?._id,
        labBatchId: labBatch?.batchId || null,
        title: formData.title.trim(),
        description: formData.description.trim(),
        instructions: formData.instructions.trim(),
        dueDate: formData.dueDate,
        dueTime: formData.dueTime || '23:59',
        maxMarks: parseFloat(formData.maxMarks),
        weightage: parseFloat(formData.weightage) || 0,
        allowLateSubmissions: formData.allowLateSubmissions,
        lateSubmissionPenalty: parseFloat(formData.lateSubmissionPenalty) || 0,
        gracePeriodMinutes: parseInt(formData.gracePeriodMinutes) || 0,
        maxFileSizeMB: parseInt(formData.maxFileSizeMB) || 10,
        allowedFileTypes: formData.allowedFileTypes.split(',').map(s => s.trim()).filter(Boolean),
        maxFilesPerSubmission: parseInt(formData.maxFilesPerSubmission) || 5,
        isGroupAssignment: formData.isGroupAssignment,
        groupSize: parseInt(formData.groupSize) || 1,
        isPublished: formData.isPublished,
        visibleToStudents: formData.visibleToStudents,
      };

      if (editingAssignment) {
        // Mode: Update Metadata
        const response = await api.put(`/api/faculty/lms/assignment/${editingAssignment._id}`, data);
        if (response.data?.success) {
          setSuccessMessage('Assignment updated successfully!');
          setTimeout(() => {
            onSuccess(response.data.data);
            onClose();
          }, 500);
        }
      } else {
        // Mode: Create New Assignment Stub First
        const response = await api.post('/api/faculty/lms/assignment', data);
        if (response.data?.success) {
          const newAssignment = response.data.data;
          const newId = newAssignment._id;

          // If there are pending files collected, process their uploads sequentially now
          if (pendingFiles.length > 0) {
            setUploadingFiles(true);
            for (const file of pendingFiles) {
              try {
                const uploadResult = await uploadFileToAssignment(file, newId);
                if (uploadResult.success) {
                  await addAttachmentToAssignment(newId, uploadResult);
                }
              } catch (fileError) {
                console.error('[AssignmentModal] Finalizing file upload error:', fileError);
              }
            }
            setUploadingFiles(false);
          }

          setSuccessMessage('Assignment created successfully with attachments!');
          setTimeout(() => {
            onSuccess(newAssignment);
            onClose();
          }, 1000);
        }
      }
    } catch (err) {
      console.error('Error saving assignment:', err);
      setError(err.response?.data?.message || 'Failed to save assignment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setError(null);
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  if (!isOpen) return null;

  const responsiveVariants = {
    hidden: { 
      y: window.innerWidth < 640 ? '100%' : 15, 
      scale: window.innerWidth < 640 ? 1 : 0.96,
      opacity: window.innerWidth < 640 ? 1 : 0 
    },
    visible: { 
      y: 0, 
      scale: 1, 
      opacity: 1,
      transition: { type: 'spring', damping: 25, stiffness: 350 }
    },
    exit: { 
      y: window.innerWidth < 640 ? '100%' : 10, 
      scale: window.innerWidth < 640 ? 1 : 0.96,
      opacity: window.innerWidth < 640 ? 1 : 0,
      transition: { duration: 0.15 }
    }
  };

  const totalAttachmentsCount = attachments.length + pendingFiles.length;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <AnimatePresence>
        <motion.div
          variants={responsiveVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="bg-white rounded-t-2xl sm:rounded-2xl max-w-3xl w-full max-h-[88vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-xl border border-slate-100"
        >
          {/* Mobile Handle Bar */}
          <div className="w-full flex justify-center py-2 sm:hidden flex-shrink-0 bg-slate-50 border-b border-slate-100">
            <div className="w-12 h-1 bg-slate-300 rounded-full" />
          </div>

          {/* Modal Header */}
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-base font-bold text-slate-800">
                {editingAssignment ? 'Modify Assignment Details' : 'Create New Assignment'}
              </h3>
              <p className="text-xs text-slate-400 font-medium mt-0.5">
                {editingAssignment ? 'Update the form configurations below' : 'Fill in the information to set up a new class assignment'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 flex-1 text-xs">
            {successMessage && (
              <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 flex items-center gap-2 font-medium">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                {successMessage}
              </div>
            )}

            {error && (
              <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 flex items-center gap-2 font-medium">
                <FiAlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                {error}
              </div>
            )}

            {assignmentId && (
              <div className="text-xs text-slate-400 bg-slate-50 Brod px-3 py-1 rounded-lg">
                Assignment ID: {assignmentId}
              </div>
            )}

            {/* Title */}
            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">
                Assignment Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                required
                placeholder="e.g., Homework 1: Core Fundamentals"
              />
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">
                Description / Question Brief <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows="3"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                required
                placeholder="Provide a summary explaining what the assignment requires..."
              />
            </div>

            {/* Instructions */}
            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">
                Special Instructions
              </label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                rows="2"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                placeholder="Add extra hints, grading criteria guidelines, or specific guidelines here..."
              />
            </div>

            {/* Deadlines Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block font-bold text-slate-500 uppercase tracking-wide">
                  Due Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="block font-bold text-slate-500 uppercase tracking-wide">
                  Due Time
                </label>
                <input
                  type="time"
                  value={formData.dueTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueTime: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                />
              </div>
            </div>

            {/* Grading Scores Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block font-bold text-slate-500 uppercase tracking-wide">
                  Max Score / Total Marks <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.maxMarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxMarks: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                  required
                  min="0"
                  step="0.5"
                  placeholder="e.g., 50"
                />
              </div>
              <div className="space-y-1">
                <label className="block font-bold text-slate-500 uppercase tracking-wide">
                  Grade Weight (%)
                </label>
                <input
                  type="number"
                  value={formData.weightage}
                  onChange={(e) => setFormData(prev => ({ ...prev, weightage: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                  min="0"
                  max="100"
                  placeholder="e.g., 15"
                />
                <p className="text-[10px] text-slate-400 font-medium">Contribution percentage towards final course marks grade.</p>
              </div>
            </div>

            {/* Late Submission Rules Container */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/60 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="allowLateSubmissions"
                  checked={formData.allowLateSubmissions}
                  onChange={(e) => setFormData(prev => ({ ...prev, allowLateSubmissions: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="allowLateSubmissions" className="text-xs font-bold text-slate-700 cursor-pointer">
                  Accept Late Submissions
                </label>
              </div>
              {formData.allowLateSubmissions && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 animate-fadeIn">
                  <div className="space-y-1">
                    <label className="block font-bold text-slate-400 uppercase tracking-wide">
                      Late Penalty Deduction (%)
                    </label>
                    <input
                      type="number"
                      value={formData.lateSubmissionPenalty}
                      onChange={(e) => setFormData(prev => ({ ...prev, lateSubmissionPenalty: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                      min="0"
                      max="100"
                      placeholder="e.g., 10"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block font-bold text-slate-400 uppercase tracking-wide">
                      Grace Period Cushion (Minutes)
                    </label>
                    <input
                      type="number"
                      value={formData.gracePeriodMinutes}
                      onChange={(e) => setFormData(prev => ({ ...prev, gracePeriodMinutes: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                      min="0"
                      placeholder="e.g., 10"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Storage Policy Banner */}
            <div className="p-3 bg-slate-100 rounded-xl border border-slate-200/60 flex items-start gap-2 text-slate-600 font-medium">
              <FiShield className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold text-slate-700 block">System Storage Submission Policies Locked</span>
                <p className="text-[10px] text-slate-400 mt-0.5 leading-normal">
                  Students are fixed to a max upload size of <strong>{formData.maxFileSizeMB} MB</strong> per file, up to <strong>{formData.maxFilesPerSubmission} files</strong> per submission. Allowed extensions: <code className="bg-slate-200 px-1 rounded">{formData.allowedFileTypes}</code>.
                </p>
              </div>
            </div>

            {/* Reference Attachment Materials Section */}
            <div className="border-2 border-dashed border-slate-200 hover:border-indigo-300 rounded-xl p-4 bg-white transition-all space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <FiPaperclip className="w-4 h-4 text-slate-400" />
                  <span className="font-bold text-slate-700">Add Reference/Question Sheets</span>
                  <span className="text-[10px] text-slate-400">({totalAttachmentsCount} attached)</span>
                </div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploadingFiles}
                  />
                  <div className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 font-bold rounded-lg text-xs transition-colors shadow-sm">
                    <FiUpload className="w-3.5 h-3.5" />
                    {uploadingFiles ? 'Uploading...' : 'Choose Files'}
                  </div>
                </label>
              </div>

              {/* Rendering list of files */}
              {totalAttachmentsCount > 0 && (
                <div className="space-y-1.5">
                  {/* Render already saved attachments */}
                  {attachments.map((file, index) => (
                    <div key={`saved-${index}`} className="flex items-center justify-between bg-slate-50 p-2.5 rounded-lg border border-slate-200/60 group">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FiFileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-700 truncate font-medium">{file.name || 'Attachment document'}</span>
                        <span className="text-[10px] text-slate-400 flex-shrink-0">({formatFileSize(file.size)})</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        {file.fileUrl && (
                          <>
                            <a
                              href={getPublicFileUrl(file.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                              title="Open File"
                            >
                              <FiEye className="w-3.5 h-3.5" />
                            </a>
                            <a
                              href={getPublicFileUrl(file.fileUrl)}
                              download
                              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                              title="Download File"
                            >
                              <FiDownload className="w-3.5 h-3.5" />
                            </a>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAttachment(index, false)}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                          title="Remove File"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Render locally staged pending files */}
                  {pendingFiles.map((file, index) => (
                    <div key={`pending-${index}`} className="flex items-center justify-between bg-amber-50/50 p-2.5 rounded-lg border border-amber-200/60 group">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FiFileText className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span className="text-slate-700 truncate font-medium">{file.name}</span>
                        <span className="text-[10px] text-amber-600 flex-shrink-0">(Staged - {formatFileSize(file.size)})</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <button
                          type="button"
                          onClick={() => removeAttachment(index, true)}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                          title="Remove File"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400 font-medium">
                Attach instruction papers, syllabus guidelines, or scoring criteria guides here. Max 10MB per file.
              </p>
            </div>

            {/* Team/Group Assignments Setup */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/60 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isGroupAssignment"
                  checked={formData.isGroupAssignment}
                  onChange={(e) => setFormData(prev => ({ ...prev, isGroupAssignment: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="isGroupAssignment" className="text-xs font-bold text-slate-700 cursor-pointer">
                  This is a Group Assignment
                </label>
              </div>
              {formData.isGroupAssignment && (
                <div className="space-y-1 animate-fadeIn">
                  <label className="block font-bold text-slate-400 uppercase tracking-wide">
                    Maximum Students per Group
                  </label>
                  <input
                    type="number"
                    value={formData.groupSize}
                    onChange={(e) => setFormData(prev => ({ ...prev, groupSize: e.target.value }))}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                    min="1"
                    placeholder="e.g., 3"
                  />
                </div>
              )}
            </div>

            {/* Visibility Settings Block */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-indigo-50/40 rounded-xl border border-indigo-100">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPublished: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="isPublished" className="text-xs font-bold text-slate-700 cursor-pointer">
                  Publish Immediately
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="visibleToStudents"
                  checked={formData.visibleToStudents}
                  onChange={(e) => setFormData(prev => ({ ...prev, visibleToStudents: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="visibleToStudents" className="text-xs font-bold text-slate-700 cursor-pointer">
                  Make Visible to Students
                </label>
              </div>
              <div className="col-span-1 sm:col-span-2 text-[10px] text-indigo-600/80 font-medium flex items-center gap-1 pt-1 border-t border-indigo-100/40">
                <FiInfo className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                <span>Students will only see assignments when both flags are marked active.</span>
              </div>
            </div>
          </form>

          {/* Action Footer */}
          <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end items-center flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 font-bold text-xs rounded-lg transition-colors shadow-sm"
              disabled={loading || uploadingFiles}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || uploadingFiles}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm shadow-indigo-100"
            >
              <FiSave className="w-3.5 h-3.5" />
              {loading ? 'Saving...' : (editingAssignment ? 'Update Assignment' : 'Create Assignment')}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AssignmentModal;